import { useContext, useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { RegulationContext } from "../../../../../App";
import ConfirmationModal from "../../../../../components/ConfirmationModal/ConfirmationModal";
import EmissionFactorService from "../../../../../core/service/EmissionFactorService";
import GHGEmissionService from "../../../../../core/service/GHGEmissionService";
import StandardService from "../../../../../core/service/StandardService";
import EmissionStatusEditor from "./EmissionStatusEditor";

const EmissionEditModal = ({
  t,
  show,
  onHide,
  entityType,
  entity,
  metricId,
  forSuperAdmin = false,
  onSaveSuccess,
}) => {
  const { glbRegulation } = useContext(RegulationContext);
  const glbRegulationId = glbRegulation?.id ?? "";
  const isDataPoint = entityType === "dataPoint";

  const [config, setConfig] = useState(null);
  const [sources, setSources] = useState([]);
  const [factors, setFactors] = useState([]);
  const [selectedSource, setSelectedSource] = useState("");
  const [selectedFactor, setSelectedFactor] = useState("");
  const [saving, setSaving] = useState(false);
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    if (!show || !entity) return;
    setSelectedSource("");
    setSelectedFactor("");
    setSources([]);
    setFactors([]);
    setValidated(false);
    // Emission Factor linking only applies at the datapoint level - the metric
    // level only manages the emission enable/table/version/scope/category config.
    if (!isDataPoint || !entity.emissionLinked) return;

    GHGEmissionService.getDatapointEmissionLinking(glbRegulationId, entity.id)
      .then((response) => {
        const data = response.data?.data || {};
        setSelectedSource(data.name || "");
        if (data.factorRef) {
          setSelectedFactor(data.factorRef);
        }
      })
      .catch(() => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, entity?.id]);

  useEffect(() => {
    if (
      !isDataPoint
      || !config?.emission
      || !config.emissionDatabase
      || !config.emissionVersionRef
      || !config.scope
      || !config.scopeCategory
    ) {
      setSources([]);
      return;
    }

    EmissionFactorService.getEmissionSourcesByVersion({
      versionId: config.emissionVersionRef,
      db: config.emissionDatabase,
      scope: config.scope,
      category: config.scopeCategory,
    })
      .then((res) => {
        if (res.status !== 200) return;
        const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        const formatted = data.map((item) => ({
          value: item.source?.name || "",
          display: item.source?.name || "",
          factors: item.factors || [],
        }));
        setSources(formatted);
      })
      .catch(() => setSources([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDataPoint, config?.emission, config?.emissionDatabase, config?.emissionVersionRef, config?.scope, config?.scopeCategory]);

  useEffect(() => {
    if (!isDataPoint || !selectedSource) {
      setFactors([]);
      return;
    }
    const sourceData = sources.find((s) => s.value === selectedSource);
    const formattedFactors = (sourceData?.factors || []).map((factor) => ({
      value: factor.id || `${factor.ghg}_${factor.efValue}`,
      display: `${factor.ghg} - ${factor.efValue}`,
    }));
    setFactors(formattedFactors);
  }, [selectedSource, sources, isDataPoint]);

  const buildMetricPayload = () => ({
    category: entity.category,
    dataPointBased: entity.dataPointUnitBased ?? false,
    emission: config.emission,
    scope: config.emission ? config.scope : null,
    scopeCategory: config.emission ? config.scopeCategory : null,
    emissionDatabase: config.emission ? config.emissionDatabase : null,
    emissionVersionRef: config.emission ? config.emissionVersionRef : null,
    helpText: entity.helpText || null,
    verticalCalcEnabled: entity.verticalCalcEnabled ?? false,
    code: entity.code,
    unitOfMeasure: entity.unitOfMeasure,
    relevantUoms: entity.relevantUoms ?? [],
    kpi: entity.kpi,
    name: entity.name,
  });

  const buildDataPointPayload = () => ({
    unitOfMeasure: entity.unitOfMeasure,
    code: entity.code,
    metricId: metricId,
    relevantUoms: entity.relevantUoms ?? [],
    emission: config.emission,
    scope: config.emission ? config.scope : "",
    scopeCategory: config.emission ? config.scopeCategory : "",
    emissionDatabase: config.emission ? config.emissionDatabase : "",
    emissionVersionRef: config.emission ? config.emissionVersionRef : "",
    frequency: entity.frequency ?? null,
    groups: entity.groups ?? null,
    altText: entity.altText || null,
    helpText: entity.helpText || null,
    fileUpload: entity.fileUpload,
    comments: entity.comments,
    applicable: entity.applicable,
    name: entity.name,
  });

  // Reaffirms the datapoint's last-saved emission enable/table/version/scope/category
  // unchanged (built from `entity`, not the live `config` form state, so an unsaved
  // in-progress edit can never leak in through the Unlink action) - Unlink must only
  // ever clear the Emission Source/Factor link, nothing else on the datapoint itself.
  const buildDataPointUnlinkPreservePayload = () => ({
    unitOfMeasure: entity.unitOfMeasure,
    code: entity.code,
    metricId: metricId,
    relevantUoms: entity.relevantUoms ?? [],
    emission: entity.emission,
    scope: entity.emission ? (entity.scope || "") : "",
    scopeCategory: entity.emission ? (entity.scopeCategory || "") : "",
    emissionDatabase: entity.emission ? (entity.emissionDatabase || entity.database || "") : "",
    emissionVersionRef: entity.emission ? (entity.emissionVersionRef || entity.versionRef || "") : "",
    frequency: entity.frequency ?? null,
    groups: entity.groups ?? null,
    altText: entity.altText || null,
    helpText: entity.helpText || null,
    fileUpload: entity.fileUpload,
    comments: entity.comments,
    applicable: entity.applicable,
    name: entity.name,
  });

  const handleSave = () => {
    if (!config) return;
    if (config.emission && !config.isValid) {
      setValidated(true);
      toast.error(t("EmissionFieldsRequired.label"), {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }
    setValidated(false);
    setSaving(true);

    const updateReq = isDataPoint
      ? StandardService.updateDataPoint(buildDataPointPayload(), entity.id, forSuperAdmin)
      : StandardService.editMetric(buildMetricPayload(), entity.id, forSuperAdmin);

    updateReq
      .then(() => {
        // Emission Factor linking is a datapoint-only concept in this modal -
        // the metric level never touches the link when saving here.
        if (!isDataPoint) return Promise.resolve();

        if (config.emission && selectedSource) {
          const payload = {
            name: selectedSource,
            database: config.emissionDatabase,
            versionRef: config.emissionVersionRef,
            factorRef: selectedFactor,
          };
          return GHGEmissionService.saveDatapointEmissionLinking(glbRegulationId, entity.id, payload);
        } else if (entity.emissionLinked && (!config.emission || !selectedSource)) {
          // emission was turned off, or the source was cleared without using the
          // dedicated Unlink action - either way, drop the stale link server-side.
          return GHGEmissionService.deleteDatapointEmissionLinking(glbRegulationId, entity.id);
        }
        return Promise.resolve();
      })
      .then(() => {
        toast.success("Success", { position: toast.POSITION.TOP_RIGHT });
        onSaveSuccess?.();
        onHide();
      })
      .catch(() => { })
      .finally(() => setSaving(false));
  };

  const handleUnlink = () => {
    setShowUnlinkConfirm(false);
    setSaving(true);

    GHGEmissionService.deleteDatapointEmissionLinking(glbRegulationId, entity.id)
      .then(() => StandardService.updateDataPoint(buildDataPointUnlinkPreservePayload(), entity.id, forSuperAdmin))
      .then(() => {
        toast.success("Success", { position: toast.POSITION.TOP_RIGHT });
        onSaveSuccess?.();
        onHide();
      })
      .catch(() => { })
      .finally(() => setSaving(false));
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title as="h5">{entity?.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {entity && (
          <EmissionStatusEditor
            entity={entity}
            glbRegulationId={glbRegulationId}
            onChange={setConfig}
            validated={validated}
          />
        )}
        {isDataPoint && config?.emission && (
          <div className="emission-factor-section mt-3 pt-3 border-top">
            <div className="d-flex align-items-center justify-content-between">
              <label className="mb-1">{t("EmissionSources.label")}</label>
              {entity?.emissionLinked && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  disabled={saving}
                  onClick={() => setShowUnlinkConfirm(true)}
                >
                  {t("Unlink.label")}
                </Button>
              )}
            </div>
            <Typeahead
              id="emission-edit-source"
              clearButton
              labelKey={(option) => option.display || option.value || option}
              selected={sources.filter((s) => (s.value || s) === selectedSource)}
              onChange={(selected) => {
                setSelectedSource(selected.length > 0 ? (selected[0].value || selected[0]) : "");
                setSelectedFactor("");
              }}
              options={sources}
              placeholder={t("Search.label")}
            />
            {selectedSource && (
              <div className="mt-2">
                <label className="mb-1">{t("EmissionFactor.label")}</label>
                <Typeahead
                  id="emission-edit-factor"
                  clearButton
                  labelKey={(option) => option.display || option.value}
                  selected={factors.filter((f) => f.value === selectedFactor)}
                  onChange={(selected) => setSelectedFactor(selected.length > 0 ? selected[0].value : "")}
                  options={factors}
                  placeholder={t("Search.label")}
                />
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          {t("Cancel.label")}
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || !config}>
          {t("Save.label")}
        </Button>
      </Modal.Footer>

      {showUnlinkConfirm && (
        <ConfirmationModal
          modalShow={showUnlinkConfirm}
          messageText={t("UnlinkEmissionFactorConfirmation.label")}
          callbackModal={(confirmed) => {
            if (confirmed) {
              handleUnlink();
            } else {
              setShowUnlinkConfirm(false);
            }
          }}
          confirmBtn={t("Confirm.label")}
          CancelBtn={t("Cancel.label")}
        />
      )}
    </Modal>
  );
};

export default withTranslation()(EmissionEditModal);
